import { CalendarMonth, DayPicker } from "react-day-picker";
import { nl } from "react-day-picker/locale";
import "react-day-picker/style.css";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { setDate } from "../appointmentSlice";
import { format } from "date-fns";
import { nl as dateFnsNl } from "date-fns/locale";
import moment from "moment";
import { HTMLAttributes, JSX, useEffect, useRef, useState } from "react";
import { setGeneralStep } from "../../helpType/helpTypeSlice";
import { GeneralStepsEnum } from "../../../common/constants/GeneralStepsEnum";
import { DamageCauseEnum } from "../../../common/constants/DamageCauseEnum";
import { scheduleScrollToStep } from "../../../common/helpers/scrollToStep";
import { appointmentCopy } from "../appointment.copy";
import {
    APPOINTMENT_WEEK_STARTS_ON,
    getMondayFirstWeekdayHeaders,
} from "../helpers/dayPickerLocale";

interface NavProps {
    onPreviousClick: () => void;
    onNextClick: () => void;
    previousMonth?: Date;
}

const Nav = ({
    onPreviousClick,
    onNextClick,
    previousMonth,
}: NavProps): JSX.Element => {
    const { agendaMoments } = useAppSelector((state) => state.appointment);
    const { activeAgendaMoment } = agendaMoments;
    const firstOptionDate =
        activeAgendaMoment?.availability?.firstOption?.day?.date;
    const thisMonth = moment(previousMonth).add(1, "month").toDate();
    const canGoPrevious =
        !!firstOptionDate &&
        moment(thisMonth).isAfter(moment(firstOptionDate).toDate());

    return (
        <div className="DayPicker-NavBar">
            {canGoPrevious && (
                <button className="button-prev" onClick={onPreviousClick}>
                    <i className={"icon-circle-button-left color-primary"} />
                </button>
            )}
            <button className="button-next" onClick={onNextClick}>
                <i className={"icon-circle-button-right color-primary"} />
            </button>
        </div>
    );
};

const MonthCaption = ({
    calendarMonth,
}: {
    calendarMonth: CalendarMonth;
}): JSX.Element => {
    const date = new Date(calendarMonth.date);
    const monthAndYearInDutch = format(date, "MMMM yyyy", {
        locale: dateFnsNl,
    });

    return (
        <div className="DayPicker-Caption">
            <div>{monthAndYearInDutch}</div>
        </div>
    );
};

// Replace DayPicker weekday children: v10 headers can stay Sunday-first
// English while the grid follows locale. Keep both on Monday-first Dutch.
const WeekDays = (props: HTMLAttributes<HTMLTableRowElement>): JSX.Element => {
    const { children: _children, className, ...rest } = props;
    const days = getMondayFirstWeekdayHeaders();

    return (
        <thead className="DayPicker-Weekdays" aria-hidden>
            <tr
                {...rest}
                className={["DayPicker-WeekdaysRow", className]
                    .filter(Boolean)
                    .join(" ")}
            >
                {days.map((day) => (
                    <th key={day} className="DayPicker-Weekday">
                        <abbr>{day}</abbr>
                    </th>
                ))}
            </tr>
        </thead>
    );
};

const AppointmentDayPicker = (): JSX.Element | null => {
    const { dossier } = useAppSelector((state) => state.root);
    const { agendaMoments } = useAppSelector((state) => state.appointment);
    const { damageCause } = useAppSelector((state) => state.vehicleInformation);
    const { activeAgendaMoment } = agendaMoments;
    const dispatch = useAppDispatch();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [selected, setSelected] = useState<Date>();
    useEffect(() => {
        if (!activeAgendaMoment) return;
        scheduleScrollToStep(() => scrollRef.current);
    }, [activeAgendaMoment]);

    const handleDaySelect = (day: Date | undefined): void => {
        setSelected(day);
        if (!day) return;
        dispatch(setGeneralStep(GeneralStepsEnum.Appointment));
        dispatch(setDate(format(day, "yyyy-MM-dd")));
    };

    if (
        activeAgendaMoment &&
        activeAgendaMoment.availability &&
        activeAgendaMoment.availability?.dates &&
        dossier
    ) {
        if (agendaMoments) {
            const disabledDays = activeAgendaMoment.availability.dates
                .filter((availability) => availability.has_capacity)
                .map((availabilityItem) => {
                    return new Date(availabilityItem.date);
                });

            const isDayDisabled = (day: Date): boolean => {
                return !disabledDays.some(
                    (disabledDay) =>
                        day.toDateString() === disabledDay.toDateString(),
                );
            };

            if (damageCause.selected === DamageCauseEnum.HAIL) {
                return null;
            }

            const firstOptionDate =
                activeAgendaMoment.availability.firstOption?.day?.date;
            const defaultMonth = firstOptionDate
                ? moment(firstOptionDate).toDate()
                : disabledDays[0];

            return (
                <div
                    className="s-appointment__group border-color-primary no-padding"
                    ref={scrollRef}
                >
                    <h1 className="s-appointment__group__title">
                        {appointmentCopy.dayPicker.title}
                    </h1>
                    <p className="small s-appointment__datepicker__text">
                        <b>{appointmentCopy.dayPicker.noticePrefix}</b>{" "}
                        {appointmentCopy.dayPicker.notice}
                    </p>
                    <div
                        className="s-appointment__datepicker"
                        style={{
                            backgroundColor: "white",
                            width: "100%",
                            height: "25rem",
                            borderRadius: ".5rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <DayPicker
                            mode="single"
                            selected={selected}
                            onSelect={handleDaySelect}
                            defaultMonth={defaultMonth}
                            disabled={isDayDisabled}
                            locale={nl}
                            weekStartsOn={APPOINTMENT_WEEK_STARTS_ON}
                            components={{
                                Nav: Nav,
                                MonthCaption: MonthCaption,
                                Weekdays: WeekDays,
                            }}
                        />
                    </div>
                </div>
            );
        } else return null;
    } else return null;
};

export default AppointmentDayPicker;
